import { motion } from 'framer-motion';
import { Mail, Calendar, Phone, MessageCircle } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';

export default function SectionContact({ data }) {
  const { title, description, steps, cta } = data;

  return (
    <section id="contact" className="section-padding bg-gradient-to-br from-black via-primary-900/20 to-black text-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{title}</h2>
          {description && <p className="text-xl text-white/90 max-w-2xl mx-auto">{description}</p>}
        </motion.div>

        {/* Étapes */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="h-full bg-white/10 backdrop-blur-sm border border-white/20 text-white">
                <div className="text-4xl font-bold text-white/40 mb-4">{step.number}</div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{step.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Contact CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <Card padding="lg" className="bg-secondary-900 border-2 border-primary-500 text-white">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">Prêt à démarrer ?</h3>
              <p className="text-secondary-200 text-lg">
                Prenons contact pour discuter de votre projet
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Email */}
              <div className="flex items-start gap-4 p-6 bg-secondary-800 border border-secondary-700 rounded-xl">
                <div className="p-3 bg-primary-900/30 text-primary-400 rounded-lg border-2 border-primary-500">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Email</h4>
                  <a
                    href={`mailto:${cta.email}`}
                    className="text-primary-400 hover:text-primary-300 transition"
                  >
                    {cta.email}
                  </a>
                </div>
              </div>

              {/* Téléphone */}
              {cta.phone && (
                <div className="flex items-start gap-4 p-6 bg-secondary-800 border border-secondary-700 rounded-xl">
                  <div className="p-3 bg-primary-900/30 text-primary-400 rounded-lg border-2 border-primary-500">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Téléphone</h4>
                    <a
                      href={`tel:${cta.phone}`}
                      className="text-primary-400 hover:text-primary-300 transition"
                    >
                      {cta.phone}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* CTA Principal */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {cta.calendly && (
                <Button
                  size="lg"
                  icon={<Calendar size={20} />}
                  onClick={() => window.open(cta.calendly, '_blank')}
                  className="flex-1 sm:flex-initial"
                >
                  Réserver un créneau
                </Button>
              )}
              <Button
                size="lg"
                variant="outline"
                icon={<MessageCircle size={20} />}
                onClick={() => (window.location.href = `mailto:${cta.email}`)}
                className="flex-1 sm:flex-initial"
              >
                Envoyer un message
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Footer note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12 text-white/70 text-sm"
        >
          <p>💼 Réponse sous 24h · 🤝 Première consultation gratuite · 🔒 Confidentialité garantie</p>
        </motion.div>
      </div>
    </section>
  );
}
